/**
 * サーバーが返す日本語の文言を、画面の言語へ置き換える表。
 *
 * なぜ表なのか（2026-08-25）
 *   サーバーは呼び手の言語を知らない。実測 …server/src で accept-language を受ける所 0、
 *   staff-app で Accept-Language を付ける所 0。
 *   ゆえに サーバーは常に日本語で返す。client 側で置き換えるしかない。
 *
 * この作りの危なさ
 *   サーバーの文言が 1 字 変わると、表に当たらず日本語がそのまま出る。
 *   **壊れても何も鳴らない。**
 *   ⇒ ゆえに 当てられなかった件数を数える（下の missCount / recordMiss）。
 *     さらに scripts/check-server-message-map.js が、
 *     サーバーの実物と この表を突き合わせて 数で出す。
 *
 * 表に無い文言は そのまま出す（日本語のまま）。消さない。
 */

type Entry = { ja: string; en: string };

/** ${} を含む文言は {0} に置き換えて持つ。前後の字で当てる。 */
const TEMPLATES: Entry[] = [
  { ja: "送信に失敗しました: {0}", en: "Sending failed: {0}" },
  {
    ja: "{0}ドライバーが受注に割り当てられていません。 配車を確定してから送信してください。",
    en: "No {0} driver is assigned to this booking. Confirm the dispatch before sending.",
  },
  {
    ja: "{0}ドライバーの LINE 連携が完了していません。 メール送信に切り替えてください。",
    en: "The {0} driver has not finished linking LINE. Switch to email instead.",
  },
  {
    ja: "{0}ドライバーのメールアドレスが登録されていません。",
    en: "No email address is registered for the {0} driver.",
  },
];

/** そのまま当てる文言。 */
const EXACT: Entry[] = [
  { ja: "受注が見つかりません。", en: "Booking not found." },
  { ja: "テンプレートが見つかりません。", en: "Template not found." },
  { ja: "送信本文がありません。", en: "There is no message body to send." },
  {
    ja: "URL 短縮 SMS は starter / premium プランでご利用可能です。 プランをアップグレードしてください。",
    en: "Shortened-URL SMS is available on the starter and premium plans. Please upgrade your plan.",
  },
  {
    ja: "URL 短縮 SMS はこの会社で有効化されていません。 マスター画面で有効化するか、 MCA サポートまでお問い合わせください。",
    en: "Shortened-URL SMS is not enabled for this account. Enable it in Masters, or contact support.",
  },
  {
    ja: "SMS 本文に {URL_PAGE} がありますが、 URL 先ページ本文が設定されていません。 テンプレートで URL 先ページ本文を設定するか、 SMS 本文の {URL_PAGE} を削除してください。",
    en: "The SMS body contains {URL_PAGE} but no landing-page text is set. Either set the landing-page text in the template, or remove {URL_PAGE} from the SMS body.",
  },
  { ja: "audience が不正です。", en: "Invalid audience." },
  { ja: "sendMethod が不正です。", en: "Invalid send method." },
  { ja: "顧客向け LINE は未対応です。", en: "LINE to guests is not supported." },
  {
    ja: "スタッフ向けは LINE グループ送信のみ対応です。",
    en: "For staff, only LINE group messages are supported.",
  },
  {
    ja: "ドライバー向け SMS は未対応です。 LINE / メールをご利用ください。",
    en: "SMS to drivers is not supported. Please use LINE or email.",
  },
  {
    ja: "ドライバー送信先 (送り / 迎え / 両方) を指定してください。",
    en: "Choose which driver to message: drop-off, pick-up, or both.",
  },
  {
    ja: "顧客の電話番号が登録されていません。",
    en: "No phone number is registered for this guest.",
  },
  {
    ja: "顧客のメールアドレスが登録されていません。",
    en: "No email address is registered for this guest.",
  },
  {
    ja: "キャストの LINE 連携が完了していません。 メール送信に切り替えてください。",
    en: "This staff member has not finished linking LINE. Switch to email instead.",
  },
  {
    ja: "キャストのメールアドレスが登録されていません。",
    en: "No email address is registered for this staff member.",
  },
  {
    ja: "キャストのスタッフ LINE グループ連携が完了していません。 キャスト編集画面の手順に従い、 グループ内で /連携 <castId> を入力してください。",
    en: "This staff member is not linked to a LINE group yet. Follow the steps on the staff edit screen and send /連携 <castId> inside the group.",
  },
  {
    ja: "タステック公式 LINE が未設定です (MCA_LINE_CHANNEL_ACCESS_TOKEN)。",
    en: "The official tasteck LINE account is not configured (MCA_LINE_CHANNEL_ACCESS_TOKEN).",
  },
  {
    ja: "LINE 設定が未完了です (Channel Access Token / 通知有効化)。",
    en: "LINE setup is incomplete (Channel Access Token / notifications enabled).",
  },
  {
    ja: "SMS 残高不足です (同時送信で残高が不足した可能性があります)。",
    en: "Not enough SMS balance (concurrent sending may have used it up).",
  },
  { ja: "重複した予約があります。", en: "There is a duplicate booking." },
  {
    ja: "存在しない源氏名に顧客を紐付けようとしています。",
    en: "You are linking a guest to a stage name that does not exist.",
  },
  { ja: "すでに受注が登録されています。", en: "This booking is already registered." },
  {
    ja: "実INを入力の際は、予約・完了ステータスで登録してください。",
    en: "To enter an actual start time, save the booking as reserved or completed.",
  },
  {
    ja: "予約・完了ステータスは、予定IN時間が必須です。",
    en: "Reserved and completed bookings require a scheduled start time.",
  },
  { ja: "予約・完了ステータスは、顧客が必須です。", en: "Reserved and completed bookings require a guest." },
  { ja: "予約・完了ステータスは、キャストが必須です。", en: "Reserved and completed bookings require a staff member." },
  { ja: "予約・完了ステータスは、店舗が必須です。", en: "Reserved and completed bookings require a venue." },
  { ja: "予約・完了ステータスは、コースが必須です。", en: "Reserved and completed bookings require a course." },
  { ja: "予約・完了ステータスは、指名が必須です。", en: "Reserved and completed bookings require a nomination type." },
  { ja: "予約・完了ステータスは、クラスが必須です。", en: "Reserved and completed bookings require a class." },
  { ja: "予約・完了ステータスは、エリアが必須です。", en: "Reserved and completed bookings require an area." },
  { ja: "予約・完了ステータスは、住所2が必須です。", en: "Reserved and completed bookings require address line 2." },
  {
    ja: "完了ステータスは、実IN時間が必須です。",
    en: "Completed bookings require an actual start time.",
  },
  {
    ja: "シフト外の時間のため予約ステータスでは登録できません。",
    en: "This time is outside the staff member's shift, so it cannot be saved as reserved.",
  },
  {
    ja: "休憩時間のため予約ステータスでは登録できません。",
    en: "This time falls in a break, so it cannot be saved as reserved.",
  },
  { ja: "迎え", en: "pick-up" },
  { ja: "送り", en: "drop-off" },
];

/** 当てられなかった文言。数で出す（log ではなく数）。 */
const misses = new Map<string, number>();

export function recordMiss(text: string): void {
  misses.set(text, (misses.get(text) || 0) + 1);
}

/** 当てられなかった件数。0 でなければ 表がサーバーとずれている。 */
export function missCount(): number {
  let n = 0;
  misses.forEach((v) => (n += v));
  return n;
}

/** 当てられなかった文言の一覧（多い順）。 */
export function missList(): Array<{ text: string; count: number }> {
  return [...misses.entries()]
    .map(([text, count]) => ({ text, count }))
    .sort((a, b) => b.count - a.count);
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * サーバーの日本語を 英語へ。当てられなければ null。
 * 呼ぶ側は null なら 元の日本語をそのまま出す（消さない）。
 */
export function translateServerMessage(text: string, lang: string): string | null {
  if (!text || lang !== "en") return null;

  const exact = EXACT.find((e) => e.ja === text);
  if (exact) return exact.en;

  for (const tpl of TEMPLATES) {
    const re = new RegExp("^" + escapeRe(tpl.ja).replace(/\\\{0\\\}/g, "(.*)") + "$");
    const m = text.match(re);
    if (m) {
      let out = tpl.en;
      for (let i = 1; i < m.length; i++) {
        const part = EXACT.find((e) => e.ja === m[i]);
        out = out.replace("{" + (i - 1) + "}", part ? part.en : m[i]);
      }
      return out;
    }
  }

  recordMiss(text);
  return null;
}

/** 表に載っている文言の数（道具から数えるため）。 */
export const entryCount = EXACT.length + TEMPLATES.length;
export const allJa = [...EXACT.map((e) => e.ja), ...TEMPLATES.map((e) => e.ja)];
